/**
 * API Client
 * Handles all HTTP requests to the cloud platform
 */

export const API_URL = import.meta.env.VITE_API_URL || 'https://api.clawdgate.com';

// Selected team ID — stored in localStorage, read by getAuthHeaders()
const TEAM_ID_KEY = 'selected_team_id';
const JWT_STORAGE_KEY = 'cloud_jwt';

/** Clear expired cloud JWT so next call falls back to Firebase token */
export function clearCloudToken() {
	localStorage.removeItem(JWT_STORAGE_KEY);
}

export function getSelectedTeamId(): string | null {
	return localStorage.getItem(TEAM_ID_KEY);
}

export function setSelectedTeamId(teamId: string | null) {
	if (teamId) {
		localStorage.setItem(TEAM_ID_KEY, teamId);
	} else {
		localStorage.removeItem(TEAM_ID_KEY);
	}
}

/** Build auth + team headers for direct fetch() calls.
 *  Prefers stored cloud JWT (has team_id, fast server-side verify)
 *  over the supplied Firebase ID token. */
export function getAuthHeaders(firebaseToken?: string): Record<string, string> {
	const cloudJwt = localStorage.getItem('cloud_jwt');
	const token = cloudJwt || firebaseToken;

	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}
	const teamId = getSelectedTeamId();
	if (teamId) {
		headers['X-Team-ID'] = teamId;
	}
	return headers;
}

class APIClient {
	private baseURL: string;
	private token: string | null = null;

	constructor(baseURL: string) {
		this.baseURL = baseURL;
	}

	setToken(token: string | null) {
		this.token = token;
	}

	private async request<T>(
		method: string,
		path: string,
		body?: any
	): Promise<T> {
		const headers: HeadersInit = {
			'Content-Type': 'application/json',
		};

		if (this.token) {
			headers['Authorization'] = `Bearer ${this.token}`;
		}

		const teamId = getSelectedTeamId();
		if (teamId) {
			headers['X-Team-ID'] = teamId;
		}

		const response = await fetch(`${this.baseURL}${path}`, {
			method,
			headers,
			body: body ? JSON.stringify(body) : undefined,
		});

		if (!response.ok) {
			if (response.status === 401) {
				// Cloud JWT expired — clear it so next call uses Firebase token
				clearCloudToken();
			}
			const error = await response.text();
			throw new Error(`HTTP ${response.status}: ${error}`);
		}

		return response.json();
	}

	async get<T>(path: string): Promise<T> {
		return this.request<T>('GET', path);
	}

	async post<T>(path: string, body?: any): Promise<T> {
		return this.request<T>('POST', path, body);
	}

	async put<T>(path: string, body?: any): Promise<T> {
		return this.request<T>('PUT', path, body);
	}

	async delete<T>(path: string): Promise<T> {
		return this.request<T>('DELETE', path);
	}

	// Auth endpoints
	auth = {
		login: (email: string, password: string) =>
			this.post('/api/v1/auth/login', { email, password }),

		register: (email: string, password: string) =>
			this.post('/api/v1/auth/register', { email, password }),
	};

	// Bot instance endpoints
	instances = {
		list: () => this.get('/api/v1/instances'),

		get: (instanceId: string) => this.get(`/api/v1/instances/${instanceId}`),

		install: (version?: string) =>
			this.post('/api/v1/instances/install', { version }),

		discover: () => this.post('/api/v1/instances/discover'),

		secure: (binaryPath: string, configPath: string) =>
			this.post('/api/v1/instances/secure', { binary_path: binaryPath, config_path: configPath }),

		start: (instanceId: string) =>
			this.post(`/api/v1/instances/${instanceId}/start`),

		stop: (instanceId: string) =>
			this.post(`/api/v1/instances/${instanceId}/stop`),

		restart: (instanceId: string) =>
			this.post(`/api/v1/instances/${instanceId}/restart`),
	};

	// Policy endpoints
	policies = {
		get: (instanceId: string) => this.get(`/api/v1/policies/${instanceId}`),

		update: (policyData: any) => this.post('/api/v1/policies', policyData),
	};

	// Event endpoints
	events = {
		list: (params?: {
			instanceId?: string;
			startTime?: number;
			endTime?: number;
			eventType?: string;
		}) => {
			const query = new URLSearchParams();
			if (params?.instanceId) query.append('instance_id', params.instanceId);
			if (params?.startTime) query.append('start_time', params.startTime.toString());
			if (params?.endTime) query.append('end_time', params.endTime.toString());
			if (params?.eventType) query.append('event_type', params.eventType);

			return this.get(`/api/v1/events?${query.toString()}`);
		},

		search: (searchQuery: string) =>
			this.post('/api/v1/events/search', { query: searchQuery }),
	};

	// Subscription endpoints
	subscriptions = {
		createCheckout: (tier: string) =>
			this.post('/api/v1/subscriptions/checkout', { tier }),

		createPortal: () => this.post('/api/v1/subscriptions/portal'),
	};
}

export const apiClient = new APIClient(API_URL);

// Initialize token from localStorage (prefer cloud JWT, fall back to legacy key)
const storedToken = localStorage.getItem('cloud_jwt') || localStorage.getItem('auth_token');
if (storedToken) {
	apiClient.setToken(storedToken);
}

export default apiClient;
